import React, { useState } from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ContractorProfile } from '../types/contractor';
import { getContractorById } from '../api/contractorProfile';
import { getFavoriteContractors } from '../api/userFavoriteContractor';
import {
  getReviewsForContractor,
  ReviewDto,
} from '../api/review';
import { getCompletedJobsForContractor } from '../api/job';
import { Job } from '../types/job';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import ContractorDetailHeader from '../components/ContractorDetailHeader';
import ContractorDetailStats from '../components/ContractorDetailStats';
import ProjectShowcase from '../components/ProjectShowcase';
import ServiceDetails from '../components/ServiceDetails';
import PerformanceMetrics, { ReviewDimension } from '../components/PerformanceMetrics';
import ReviewsSummary from '../components/ReviewsSummary';
import FixedBottomActionArea from '../components/FixedBottomActionArea';
import { useAuth } from '../context/AuthContext';

export async function contractorDetailLoader({
  params,
}: {
  params: Readonly<Record<string, string | undefined>>;
}) {
  const { contractorId } = params;
  if (!contractorId) throw new Error('Contractor ID is required.');

  const [contractor, reviews] = await Promise.all([
    getContractorById(contractorId),
    getReviewsForContractor(Number(contractorId)),
  ]);

  const [completedJobs] = await Promise.all([
    getCompletedJobsForContractor(contractor.id),
  ]);

  let isFavorited = false;
  try {
    const favorites = await getFavoriteContractors();
    isFavorited = favorites.some((f) => f.contractorProfileId === contractor.id);
  } catch {
    // not logged in
  }

  return { contractor, isFavorited, reviews, completedJobs };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) > 1 ? 's' : ''} ago`;
}

function toReviewSummaryItem(r: ReviewDto) {
  const avg = Math.round((r.qualityRating + r.reliabilityRating + r.communicationRating + r.pricingRating) / 4);
  return {
    reviewerName: r.reviewerDisplayName || 'Anonymous',
    project: '',
    timeAgo: timeAgo(r.timestamp),
    rating: avg,
    comment: r.comment,
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const credentialMeta: Record<string, { label: string; icon: string }> = {
  Licence:         { label: 'Licence',            icon: 'badge' },
  Insurance:       { label: 'Insurance',           icon: 'policy' },
  HealthAndSafety: { label: 'Health & Safety',     icon: 'health_and_safety' },
  BBBEE:           { label: 'B-BBEE',              icon: 'diversity_3' },
  NHBRC:           { label: 'NHBRC Registered',    icon: 'home_work' },
};

function computeReviewDimensions(reviews: ReviewDto[]): ReviewDimension[] {
  if (reviews.length === 0) return [];
  const avg = (fn: (r: ReviewDto) => number) =>
    reviews.reduce((sum, r) => sum + fn(r), 0) / reviews.length;
  return [
    { label: 'Quality',       avg: avg((r) => r.qualityRating),       icon: 'star' },
    { label: 'Reliability',   avg: avg((r) => r.reliabilityRating),   icon: 'verified' },
    { label: 'Communication', avg: avg((r) => r.communicationRating), icon: 'chat' },
    { label: 'Pricing',       avg: avg((r) => r.pricingRating),       icon: 'payments' },
  ];
}

function buildComplianceItems(contractor: ContractorProfile) {
  return (contractor.credentials ?? [])
    .filter((c) => c.status === 'Confirmed')
    .map((c) => {
      const meta = credentialMeta[c.type] ?? { label: c.type, icon: 'verified_user' };
      return {
        name: meta.label,
        details: c.registrationNumber ?? '',
        status: 'CHECKED' as const,
        icon: meta.icon,
      };
    });
}


// ─── Completed Jobs Section ───────────────────────────────────────────────────

interface CompletedJobsListProps {
  jobs: Job[];
}

const CompletedJobsList: React.FC<CompletedJobsListProps> = ({ jobs }) => {
  if (jobs.length === 0) return null;

  return (
    <section className="mt-6">
      <h3 className="font-title-lg text-on-surface mb-4 flex items-center gap-2">
        <span className="material-symbols-outlined text-primary text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>
          task_alt
        </span>
        Completed Jobs
        <span className="text-sm font-normal text-on-surface-variant ml-1">({jobs.length})</span>
      </h3>
      <div className="space-y-3">
        {jobs.map((job) => (
          <Link
            key={job.id}
            to={`/jobs/${job.id}`}
            className="flex items-start justify-between gap-4 bg-surface-container-low rounded-xl p-4 hover:bg-surface-container transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="font-label-bold text-on-surface truncate">{job.title}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-on-surface-variant">
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-[13px]">location_on</span>
                  {job.location}
                </span>
                {job.categoryName && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">category</span>
                    {job.categoryName}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-[13px]">calendar_today</span>
                  {new Date(job.createdAt).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
            <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-label-bold bg-surface-container text-on-surface-variant">
              Completed
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const ContractorDetailPage: React.FC = () => {
  const {
    contractor,
    isFavorited: initialIsFavorited,
    reviews: initialReviews,
    completedJobs,
  } = useLoaderData() as {
    contractor: ContractorProfile;
    isFavorited: boolean;
    reviews: ReviewDto[];
    completedJobs: Job[];
  };

  const { currentUser } = useAuth();
  const [reviews] = useState<ReviewDto[]>(initialReviews);

  const handleToggleFavorite = (contractorId: number, isFavorited: boolean) => {
    console.log(`Contractor ${contractorId} favorite toggled to ${isFavorited}`);
  };

  const reviewSummaryItems = reviews.map(toReviewSummaryItem);
  const reviewDimensions = computeReviewDimensions(reviews);

  if (!contractor) {
    return (
      <div className="container mx-auto p-4 text-center text-gray-500">
        Contractor not found.
      </div>
    );
  }

  const location = [contractor.city, contractor.province].filter(Boolean).join(', ');

  const primaryCategory = contractor.categories?.[0] ?? '';
  const pageTitle = [contractor.companyName, primaryCategory && location ? `${primaryCategory} in ${location}` : primaryCategory || location]
    .filter(Boolean)
    .join(' - ') + ' | Buildon';

  const rawDescription = contractor.bio?.trim()
    ? contractor.bio.length > 155
      ? contractor.bio.slice(0, 152) + '...'
      : contractor.bio
    : [
        contractor.isVerified ? 'Verified' : null,
        primaryCategory || null,
        location ? `in ${location}` : null,
        contractor.yearsOfExperience ? `with ${contractor.yearsOfExperience} years experience` : null,
        contractor.reviewCount ? `Rated ${contractor.averageRating?.toFixed(1)}/5 from ${contractor.reviewCount} reviews on Buildon.` : null,
      ].filter(Boolean).join(' ');

  const ogImage = contractor.profilePictureUrl || 'https://buildon.co.za/buildonLogo.png';
  const canonicalUrl = `https://buildon.co.za/contractors/${contractor.id}`;

  const serviceAreaNames = contractor.serviceAreaDetails?.map(sa => sa.townName) ?? contractor.serviceAreas ?? [];
  const showcaseProjects = (contractor.galleryImages ?? []).map((img, i) => ({
    id: String(img.id ?? i),
    imageUrl: img.url,
    altText: img.caption || 'Gallery image',
    title: img.caption,
  }));

  return (
    <div className="bg-surface text-on-surface min-h-screen pb-24 md:pb-0">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={rawDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={rawDescription} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={rawDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <TopAppBar />
      <main className="pb-32 md:pt-24 md:pb-12 px-container-margin max-w-7xl mx-auto">
        {/* Mobile Layout */}
        <div className="md:hidden">
          <ContractorDetailHeader
            contractorName={contractor.companyName || ''}
            companyName={contractor.companyName || ''}
            rating={contractor.averageRating || 0}
            reviews={contractor.reviewCount || 0}
            profileImageUrl={contractor.profilePictureUrl || `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23e5e7eb'/%3E%3Ccircle cx='75' cy='60' r='28' fill='%239ca3af'/%3E%3Cellipse cx='75' cy='130' rx='48' ry='32' fill='%239ca3af'/%3E%3C/svg%3E`}
            isVerified={contractor.isVerified ?? false}
            website={contractor.website}
            location={location || undefined}
            serviceAreas={serviceAreaNames}
            isMobile={true}
            contractorId={contractor.id}
            initialIsFavorited={initialIsFavorited}
            onToggleFavorite={handleToggleFavorite}
          />
          <ContractorDetailStats
            yearsExperience={contractor.yearsOfExperience || 0}
            projectSize="85+"
            completionRate={100}
            isMobile={true}
          />
          {contractor.bio && (
            <section className="px-container-margin mt-stack-xl">
              <h3 className="font-headline-sm text-headline-sm text-primary mb-2">About</h3>
              <p className="text-body-md text-on-surface-variant whitespace-pre-line">{contractor.bio}</p>
            </section>
          )}
          <ProjectShowcase projects={showcaseProjects} isMobile={true} />
          <ServiceDetails
            coreServices={contractor.categories ?? []}
            compliance={buildComplianceItems(contractor)}
            isMobile={true}
          />
          <PerformanceMetrics
            dimensions={reviewDimensions}
            reviewCount={reviews.length}
            isMobile={true}
          />
          <div className="px-container-margin">
            <CompletedJobsList jobs={completedJobs} />
          </div>
          <ReviewsSummary reviews={reviewSummaryItems} isMobile={true} />
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-12 gap-gutter">
          <ContractorDetailHeader
            contractorName={contractor.companyName || ''}
            companyName={contractor.companyName || ''}
            rating={contractor.averageRating || 0}
            reviews={contractor.reviewCount || 0}
            profileImageUrl={contractor.profilePictureUrl || `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23e5e7eb'/%3E%3Ccircle cx='75' cy='60' r='28' fill='%239ca3af'/%3E%3Cellipse cx='75' cy='130' rx='48' ry='32' fill='%239ca3af'/%3E%3C/svg%3E`}
            isVerified={contractor.isVerified ?? false}
            website={contractor.website}
            location={location || undefined}
            serviceAreas={serviceAreaNames}
            isDesktop={true}
            contractorId={contractor.id}
            initialIsFavorited={initialIsFavorited}
            onToggleFavorite={handleToggleFavorite}
            shareUrl={`${window.location.origin}/contractors/${contractor.id}`}
          />
          <div className="md:col-span-8 space-y-gutter">
            {contractor.bio && (
              <section className="bg-surface-container-lowest rounded-xl shadow-sm p-stack-lg border border-outline-variant">
                <h3 className="font-headline-sm text-headline-sm text-primary mb-2">About</h3>
                <p className="text-body-md text-on-surface-variant whitespace-pre-line">{contractor.bio}</p>
              </section>
            )}
            <ProjectShowcase projects={showcaseProjects} isDesktop={true} />
            <ServiceDetails
              coreServices={contractor.categories ?? []}
              compliance={buildComplianceItems(contractor)}
              isDesktop={true}
            />
            <PerformanceMetrics
              dimensions={reviewDimensions}
              reviewCount={reviews.length}
              isDesktop={true}
            />
            <CompletedJobsList jobs={completedJobs} />
            <ReviewsSummary reviews={reviewSummaryItems} isDesktop={true} />
          </div>
        </div>
      </main>
      <FixedBottomActionArea contractorId={contractor.id} contractorName={contractor.companyName || ''} />
      <BottomNavBar />
    </div>
  );
};

export default ContractorDetailPage;
